import { Mapping } from 'src/containers/ProjectDiagram/types/data';
import { LinkData } from 'src/containers/ProjectDiagram/types/model';
import {
  isMapping,
  isRelationshipMapping,
} from 'src/containers/ProjectDiagram/utils/data';
import { createRelationshipMapping } from 'src/containers/ProjectDiagram/utils/mapping/createRelationshipMapping';
import { getModelConceptClassValue } from 'src/containers/ProjectDiagram/utils/modelConceptClass/createModelConceptClass';
import { Project, ProjectsState } from 'src/store/state/projects';
import { ReduxState } from 'src/store/types';

/**
 * Converts existing relationship mappings that are doubly connected to singly connected relationship mappings
 */
// prevState must be typed as `any` since it's an older version of the Redux State

export const v19 = (prevState: any): ReduxState => {
  const nextState = { ...prevState };

  if (nextState.projects) {
    nextState.projects = v19Projects(prevState.projects);
  }

  return nextState;
};

const v19Projects = (prevProjectsState: any): ProjectsState => {
  const projectsById: Record<string, Project> = {};

  Object.entries(
    prevProjectsState.projectsById as Record<string, Project>
  ).forEach(([projectId, project]) => {
    const { diagram } = project;
    if (!diagram) {
      projectsById[projectId] = project;
      return;
    }

    const mappingsByKey = diagram.model.linkDataArray.reduce<
      Record<string, Mapping>
    >((acc, linkData) => {
      if (isMapping(linkData)) {
        acc[linkData.key] = linkData;
      }
      return acc;
    }, {});

    const updatedLinkDataArray: LinkData[] = [];
    diagram.model.linkDataArray.forEach((linkData: any) => {
      if (isRelationshipMapping(linkData)) {
        if (linkData.from && linkData.to) {
          const fromMapping = mappingsByKey[linkData.from];
          if (fromMapping && fromMapping.to) {
            updatedLinkDataArray.push(
              createRelationshipMapping({
                fromClassIri: getModelConceptClassValue(fromMapping.to),
                fromPrimaryIdentifier: {
                  baseIri: fromMapping.primaryIdentifier.baseIri,
                  parts: [...fromMapping.primaryIdentifier.parts],
                },
                relationshipIri: linkData.relationshipIri,
                to: linkData.to,
              })
            );
          }
          const toMapping = mappingsByKey[linkData.to];
          if (toMapping && toMapping.to) {
            updatedLinkDataArray.push(
              createRelationshipMapping({
                from: linkData.from,
                relationshipIri: linkData.relationshipIri,
                toClassIri: getModelConceptClassValue(toMapping.to),
                toPrimaryIdentifier: {
                  baseIri: toMapping.primaryIdentifier.baseIri,
                  parts: [...toMapping.primaryIdentifier.parts],
                },
              })
            );
          }
          return;
        }
      }

      updatedLinkDataArray.push(linkData);
    });

    projectsById[projectId] = {
      ...project,
      diagram: {
        ...diagram,
        model: {
          ...diagram.model,
          linkDataArray: updatedLinkDataArray,
        },
      },
    };
  });

  return { ...prevProjectsState, projectsById };
};
